import React, { useState, useEffect } from 'react';
import { History, Clock, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import api from '@/utils/api';
import { toast } from 'sonner';

export default function PromptVersionHistory({ agentId }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [versions, setVersions] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(null);

  const loadVersions = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/agents/${agentId}/prompt-versions`);
      setVersions(response.data.versions);
    } catch (error) {
      toast.error('Failed to load version history');
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (isOpen) => {
    setOpen(isOpen);
    if (isOpen) {
      loadVersions();
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="rounded-full" data-testid="version-history-button">
          <History className="w-4 h-4 mr-2" />
          Version History
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-5xl max-h-[80vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle>Prompt Version History</DialogTitle>
          <DialogDescription>
            Track changes to your agent's prompt configuration over time
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : versions.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <History className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No version history yet</p>
            <p className="text-sm">Versions are saved when you update agent settings</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4" style={{ height: '500px' }}>
            {/* Version List */}
            <ScrollArea className="border rounded-lg">
              <div className="p-4 space-y-2">
                {versions.map((version) => (
                  <div
                    key={version.version_id}
                    onClick={() => setSelectedVersion(version)}
                    className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedVersion?.version_id === version.version_id
                        ? 'border-primary bg-primary/5'
                        : 'hover:border-primary/50 hover:bg-slate-50'
                    }`}
                    data-testid={`version-${version.version_number}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <Badge variant="secondary">v{version.version_number}</Badge>
                      <Badge variant="outline">
                        {version.fields_snapshot.prompt_variant || 'A'}
                      </Badge>
                    </div>
                    <div className="flex items-center text-sm text-muted-foreground space-x-3">
                      <div className="flex items-center">
                        <Clock className="w-3 h-3 mr-1" />
                        {formatDate(version.created_at)}
                      </div>
                      <div className="flex items-center">
                        <User className="w-3 h-3 mr-1" />
                        {version.created_by_name}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>

            {/* Version Details */}
            <ScrollArea className="border rounded-lg">
              {selectedVersion ? (
                <div className="p-4 space-y-4">
                  <div>
                    <h3 className="font-semibold mb-2">Version {selectedVersion.version_number}</h3>
                    <div className="space-y-2 text-sm">
                      <div>
                        <span className="text-muted-foreground">Response Style:</span>{' '}
                        <Badge variant="secondary">{selectedVersion.fields_snapshot.response_style}</Badge>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Prompt Variant:</span>{' '}
                        <Badge variant="secondary">{selectedVersion.fields_snapshot.prompt_variant || 'A'}</Badge>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-medium text-sm mb-2">Full System Prompt</h4>
                    <div className="bg-slate-50 rounded-lg p-3 font-mono text-xs whitespace-pre-wrap max-h-80 overflow-y-auto border">
                      {selectedVersion.system_prompt}
                    </div>
                  </div>

                  <div>
                    <h4 className="font-medium text-sm mb-2">Configuration Snapshot</h4>
                    <div className="space-y-2 text-xs">
                      {selectedVersion.fields_snapshot.business_summary && (
                        <div>
                          <span className="text-muted-foreground font-medium">Business Summary:</span>
                          <p className="mt-1 text-foreground">{selectedVersion.fields_snapshot.business_summary}</p>
                        </div>
                      )}
                      {selectedVersion.fields_snapshot.allowed_topics?.length > 0 && (
                        <div>
                          <span className="text-muted-foreground font-medium">Allowed Topics:</span>
                          <div className="mt-1 flex flex-wrap gap-1">
                            {selectedVersion.fields_snapshot.allowed_topics.map((topic, i) => (
                              <Badge key={i} variant="outline" className="text-xs">{topic}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      {selectedVersion.fields_snapshot.disallowed_topics?.length > 0 && (
                        <div>
                          <span className="text-muted-foreground font-medium">Disallowed Topics:</span>
                          <div className="mt-1 flex flex-wrap gap-1">
                            {selectedVersion.fields_snapshot.disallowed_topics.map((topic, i) => (
                              <Badge key={i} variant="secondary" className="text-xs">{topic}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-full text-muted-foreground">
                  <p>Select a version to view details</p>
                </div>
              )}
            </ScrollArea>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
